import { useEffect, useMemo, useState } from "react";
import { useAudioRecorder } from "../hooks/useAudioRecorder";
import { useLiveTranscription } from "../hooks/useLiveTranscription";
import { transcribeAudio, type TranscribeResponse } from "../lib/http/speechApi";
import { costProjections, formatCost, formatDuration, formatMs } from "../lib/format";
import VoiceSearchBar from "../components/VoiceSearchBar";

interface RunRecord {
  id: number;
  data: TranscribeResponse;
  clientLatencyMs: number;
  at: Date;
}

export default function TestSpeechPage() {
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { status, errorMessage, start, stop } = useAudioRecorder();
  const isRecording = status === "recording";

  const totals = useMemo(() => {
    const totalCost = runs.reduce((sum, r) => sum + (r.data.cost_usd ?? 0), 0);
    const avgServerLatency =
      runs.length === 0 ? 0 : runs.reduce((sum, r) => sum + (r.data.latency_ms ?? 0), 0) / runs.length;
    const avgClientLatency =
      runs.length === 0 ? 0 : runs.reduce((sum, r) => sum + r.clientLatencyMs, 0) / runs.length;
    const totalTokens = runs.reduce((sum, r) => sum + (r.data.total_tokens ?? 0), 0);
    return { totalCost, avgServerLatency, avgClientLatency, totalTokens, count: runs.length };
  }, [runs]);

  const handleRecordToggle = async () => {
    if (isRecording) {
      const blob = await stop();
      if (!blob) return;
      setTranscribing(true);
      setError(null);
      try {
        const { data, clientLatencyMs } = await transcribeAudio(blob, { includeWords: true });
        setRuns((prev) => [{ id: Date.now(), data, clientLatencyMs, at: new Date() }, ...prev]);
      } catch (err: any) {
        setError(err?.response?.data?.detail || err?.message || "Transcription failed");
      } finally {
        setTranscribing(false);
      }
      return;
    }
    setError(null);
    await start();
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900">Speech-to-Text test bench</h1>
      <p className="mt-1 text-sm text-gray-500">
        Record a phrase (try Hinglish, e.g. "mujhe spain package chahiye"), check the transcript,
        latency, confidence and cost per call. Swap providers via <code className="rounded bg-gray-100 px-1">STT_PROVIDER</code> in
        the backend .env — no code change needed.
      </p>

      {/* Live captions — real WebSocket streaming to Deepgram, words appear as you talk */}
      <div className="mt-6">
        <h2 className="mb-2 text-sm font-semibold text-gray-700">Live captions (Deepgram streaming — Claude/ChatGPT-style)</h2>
        <LiveCaptionsPanel />
      </div>

      {/* Normal search bar w/ voice, reusable component */}
      <div className="mt-10">
        <h2 className="mb-2 text-sm font-semibold text-gray-700">Search bar with voice (drop-in component, batch)</h2>
        <VoiceSearchBar
          onSearch={(q) => console.log("search submitted:", q)}
        />
      </div>

      {/* Dedicated mic test harness w/ run history */}
      <div className="mt-10 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">Test harness (run history)</h2>
          <button
            type="button"
            onClick={handleRecordToggle}
            disabled={transcribing}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-colors ${
              isRecording ? "bg-red-500 animate-pulse" : transcribing ? "bg-gray-300" : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3z" />
              <path d="M17 11a1 1 0 10-2 0 3 3 0 01-6 0 1 1 0 10-2 0 5 5 0 004 4.9V18H9a1 1 0 100 2h6a1 1 0 100-2h-2v-2.1a5 5 0 004-4.9z" />
            </svg>
            {isRecording ? "Stop recording" : transcribing ? "Transcribing…" : "Start recording"}
          </button>
        </div>

        {(errorMessage || error) && <p className="mt-3 text-sm text-red-600">{errorMessage || error}</p>}

        {runs.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat label="Runs" value={String(totals.count)} />
            <Stat label="Total cost" value={formatCost(totals.totalCost)} />
            <Stat label="Total tokens" value={String(totals.totalTokens)} />
            <Stat label="Avg server latency" value={formatMs(totals.avgServerLatency)} />
            <Stat label="Avg client latency" value={formatMs(totals.avgClientLatency)} />
          </div>
        )}

        <div className="mt-4 space-y-3">
          {runs.length === 0 && !transcribing && (
            <p className="text-sm text-gray-400">No runs yet — hit "Start recording" and say something.</p>
          )}
          {runs.map((run) => (
            <RunCard key={run.id} run={run} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-gray-50 p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function RunCard({ run }: { run: RunRecord }) {
  const { data, clientLatencyMs, at } = run;
  const proj = costProjections(data.cost_usd, data.duration_seconds);

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm font-medium text-gray-900">"{data.text || "(empty transcript)"}"</p>
        <span className="shrink-0 text-xs text-gray-400">{at.toLocaleTimeString()}</span>
      </div>

      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <Badge color="gray">{data.provider} · {data.model}</Badge>
        <Badge color="blue">⏱ client {formatMs(clientLatencyMs)} · server {formatMs(data.latency_ms)}</Badge>
        <Badge color="green">💰 {formatCost(data.cost_usd)} for {formatDuration(data.duration_seconds)} audio</Badge>
        <Badge color="purple">
          🎯 {data.confidence !== null ? `${(data.confidence * 100).toFixed(0)}% confidence` : "confidence n/a"}
        </Badge>
        {data.language && <Badge color="gray">lang: {data.language}</Badge>}
        {data.total_tokens !== null && (
          <Badge color="gray">
            🔢 {data.total_tokens} tokens (in {data.input_tokens ?? "?"} / out {data.output_tokens ?? "?"})
          </Badge>
        )}
      </div>

      {proj && (
        <p className="mt-2 text-xs text-gray-500">
          At this rate: <b>{formatCost(proj.per5s)}</b> for 5s · <b>{formatCost(proj.per10s)}</b> for 10s ·{" "}
          <b>{formatCost(proj.perMin)}</b> for 1 min of audio.
        </p>
      )}

      {data.low_confidence_words.length > 0 && (
        <p className="mt-2 text-xs text-amber-700">⚠ low-confidence words: {data.low_confidence_words.join(", ")}</p>
      )}
    </div>
  );
}

function Badge({ color, children }: { color: "gray" | "blue" | "green" | "purple"; children: React.ReactNode }) {
  const map: Record<string, string> = {
    gray: "bg-gray-100 text-gray-600",
    blue: "bg-blue-50 text-blue-700",
    green: "bg-green-50 text-green-700",
    purple: "bg-purple-50 text-purple-700",
  };
  return <span className={`rounded-full px-2 py-1 ${map[color]}`}>{children}</span>;
}

function LiveCaptionsPanel() {
  const { status, finalText, interimText, errorMessage, totalDurationS, estimatedCostUsd, start, stop } =
    useLiveTranscription();
  const [elapsedMs, setElapsedMs] = useState(0);
  const isListening = status === "listening" || status === "connecting";

  useEffect(() => {
    if (status !== "listening") return;
    const startedAt = Date.now();
    const id = setInterval(() => setElapsedMs(Date.now() - startedAt), 200);
    return () => clearInterval(id);
  }, [status]);

  useEffect(() => {
    if (status === "connecting") setElapsedMs(0);
  }, [status]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => (isListening ? stop() : start())}
          disabled={status === "stopping"}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-colors ${
            isListening ? "bg-red-500 animate-pulse" : status === "stopping" ? "bg-gray-300" : "bg-indigo-600 hover:bg-indigo-700"
          }`}
        >
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3z" />
            <path d="M17 11a1 1 0 10-2 0 3 3 0 01-6 0 1 1 0 10-2 0 5 5 0 004 4.9V18H9a1 1 0 100 2h6a1 1 0 100-2h-2v-2.1a5 5 0 004-4.9z" />
          </svg>
          {status === "connecting" ? "Connecting…" : isListening ? "Stop" : status === "stopping" ? "Stopping…" : "Start talking"}
        </button>
        {status === "listening" && (
          <span className="text-sm font-mono text-gray-500">{formatMs(elapsedMs)}</span>
        )}
      </div>

      {errorMessage && <p className="mt-3 text-sm text-red-600">{errorMessage}</p>}

      <div className="mt-4 min-h-[4rem] rounded-lg bg-gray-50 p-4 text-lg leading-relaxed">
        {!finalText && !interimText && (
          <span className="text-gray-400">
            {isListening ? "Listening…" : "Hit \"Start talking\" — words appear as you speak."}
          </span>
        )}
        <span className="text-gray-900">{finalText}</span>{" "}
        <span className="text-gray-400 italic">{interimText}</span>
      </div>

      {totalDurationS !== null && (
        <p className="mt-2 text-xs text-gray-500">
          Session: {formatDuration(totalDurationS)} of speech · estimated cost {formatCost(estimatedCostUsd)}
        </p>
      )}
    </div>
  );
}
